/**
 * Shared MediaPipe Tasks Vision loaders. Used by both PoseCanvas (single
 * athlete) and MultiPlayerPoseCanvas (pickleball doubles up to 4 players).
 *
 * Loaded via CDN (Apache 2.0) — no npm install. Each variant is memoized so
 * the same instance is shared across components.
 *
 * Variants:
 *   "full"  — default for single-athlete close-range capture (≤ 15ft).
 *   "heavy" — higher accuracy at small/distant figures. Used for multi-
 *             player doubles capture where the coach films from the corner
 *             and the far players are tiny in frame.
 *
 * `loadObjectDetector()` is the EfficientDet-Lite person detector used as
 * the first stage of the doubles tracking pipeline — it gives us reliable
 * person bounding boxes at distance, which we then crop and pass to
 * PoseLandmarker for high-quality landmarks per player.
 */

const promises = { full: null, heavy: null };
let visionPromise = null;
let visionModule = null;

const MODEL_URLS = {
  full: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task",
  heavy: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/latest/pose_landmarker_heavy.task",
};

async function getVision() {
  if (visionPromise) return visionPromise;
  visionPromise = (async () => {
    const mod = await import(
      /* webpackIgnore: true */ "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.20/+esm"
    );
    visionModule = mod;
    const vision = await mod.FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.20/wasm"
    );
    return vision;
  })();
  return visionPromise;
}

export async function loadPoseLandmarker(numPoses = 5, variant = "full") {
  const key = MODEL_URLS[variant] ? variant : "full";
  if (promises[key]) return promises[key];

  promises[key] = (async () => {
    const vision = await getVision();
    const { PoseLandmarker } = visionModule;
    const isHeavy = key === "heavy";
    return PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URLS[key],
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numPoses,
      minPoseDetectionConfidence: isHeavy ? 0.2 : 0.3,
      minPosePresenceConfidence: isHeavy ? 0.2 : 0.3,
      minTrackingConfidence: isHeavy ? 0.2 : 0.3,
    });
  })();
  return promises[key];
}

let objectDetectorPromise = null;
/**
 * EfficientDet-Lite0 person detector. Far better at finding small/distant
 * figures than the pose model's built-in person detector. Used as the
 * first stage of the doubles 2-stage pipeline:
 *   1. ObjectDetector → person bounding boxes
 *   2. Tracker assigns persistent IDs across frames
 *   3. PoseLandmarker runs per-crop for high-quality landmarks
 */
export async function loadObjectDetector(maxResults = 8) {
  if (objectDetectorPromise) return objectDetectorPromise;
  objectDetectorPromise = (async () => {
    const vision = await getVision();
    const { ObjectDetector } = visionModule;
    return ObjectDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      maxResults,
      scoreThreshold: 0.3,
      categoryAllowlist: ["person"],
    });
  })();
  return objectDetectorPromise;
}

/**
 * Image-mode PoseLandmarker — used to run pose detection on a CROP of the
 * frame (not the whole frame). Cropping zooms in on a single player so
 * landmark accuracy at distance jumps dramatically. We can't reuse the
 * VIDEO-mode landmarker because MediaPipe requires a single mode per
 * instance and the running mode is locked at creation time.
 */
let imageLandmarkerPromise = null;
export async function loadImagePoseLandmarker() {
  if (imageLandmarkerPromise) return imageLandmarkerPromise;
  imageLandmarkerPromise = (async () => {
    const vision = await getVision();
    const { PoseLandmarker } = visionModule;
    return PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URLS.full,
        delegate: "GPU",
      },
      runningMode: "IMAGE",
      numPoses: 1,
      minPoseDetectionConfidence: 0.3,
      minPosePresenceConfidence: 0.3,
      minTrackingConfidence: 0.3,
    });
  })();
  return imageLandmarkerPromise;
}
